import React, { useMemo } from "react";
import { Tabs } from "antd";
import type { TabsProps } from "antd";
import { useTranslation } from "react-i18next";
import { useAbility } from "config/abilityContext";

type TabType = "inbox" | "sent" | "getAll";

interface MessageSidebarProps {
  selectedType: TabType;
  onTypeChange: (type: TabType) => void;
}

const MessageSidebar: React.FC<MessageSidebarProps> = ({ selectedType, onTypeChange }) => {
  const { t } = useTranslation();
  const ability = useAbility();

  const canGetAll = ability?.can("getAll", "Message");
  const items = useMemo<TabsProps["items"]>(() => {
    const base: TabsProps["items"] = [
      {
        key: "inbox",
        label: (
          <span>
            <i className="ti ti-download" style={{ marginRight: 8 }} />
            {t("inbox")}
          </span>
        ),
      },
      {
        key: "sent",
        label: (
          <span>
            <i className="ti ti-send" style={{ marginRight: 8 }} />
            {t("sent_mail")}
          </span>
        ),
      },
    ];

    if (canGetAll) {
      base.push({
        key: "getAll",
        label: (
          <span>
            <i className="ti ti-folder-search" style={{ marginRight: 8 }} />
            {t("all_messages_instance")}
          </span>
        ),
      });
    }

    return base;
  }, [t, canGetAll]);

  // If the current selection is not allowed, fall back to "inbox"
  const safeActiveKey: TabType =
    selectedType === "getAll" && !canGetAll ? "inbox" : selectedType;

  return (
    <Tabs
      activeKey={safeActiveKey}
      onChange={(key) => onTypeChange(key as TabType)}
      tabBarStyle={{ marginBottom: 0 }}
      items={items}
    />
  );
};

export default MessageSidebar;
